import React, { useEffect, useState } from 'react';
import ToastNotification from './toast-notification';
import {
  onForegroundMessage,
  enablePush,
  pushPermission,
  hasLocalToken,
} from '../services/firebasePush';
import { logger } from '../utils/logger';

/** Whether a validated user session is cached (set by App /auth/me + login flows). */
function isLoggedIn(): boolean {
  return !!localStorage.getItem('srs_user');
}

/**
 * App-wide push bootstrap + foreground handler for the website (tenants).
 *
 * When a user is logged in it auto-enables web push: requests browser
 * notification permission (only while still undecided — a denied prompt is
 * permanent, so it is never re-triggered) and registers this browser's FCM
 * token. When permission is already granted it silently (re)registers the token.
 * Once opted in, incoming foreground FCM messages surface as a toast. This mirrors
 * the admin portal's PushListener so tenants receive all their notifications
 * (messages, enquiries, KYC, property updates, etc.) as web push.
 */
export default function PushManager() {
  const [toast, setToast] = useState<{ message: string } | null>(null);
  const [authed, setAuthed] = useState<boolean>(isLoggedIn);

  // Track login/logout — `userUpdated` fires on login, registration and session
  // recovery (same tab); `storage` mirrors it across tabs.
  useEffect(() => {
    const sync = () => setAuthed(isLoggedIn());
    window.addEventListener('userUpdated', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('userUpdated', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    if (!authed) return;

    let active = true;
    let unsubscribe = () => {};

    (async () => {
      const perm = pushPermission();
      // Nothing to do if unsupported or the user has permanently blocked us.
      if (perm === 'unsupported' || perm === 'denied') return;

      // Auto opt-in on login. Shows the native prompt only when permission is
      // still 'default'; otherwise just ensures the token is registered.
      if (!(perm === 'granted' && hasLocalToken())) {
        const res = await enablePush();
        if (!res.ok) {
          logger.warn('Auto push-enable did not complete', res.reason);
          return;
        }
      }

      const fn = await onForegroundMessage((payload) => {
        const title = payload.notification?.title || (payload.data as any)?.title || 'Notification';
        const body = payload.notification?.body || (payload.data as any)?.body || '';
        setToast({ message: body ? `${title}: ${body}` : title });
        window.dispatchEvent(new CustomEvent('fcm-message', { detail: payload }));
      });

      if (active) unsubscribe = fn;
      else fn();
    })();

    return () => {
      active = false;
      unsubscribe();
    };
  }, [authed]);

  if (!toast) return null;
  return (
    <ToastNotification
      message={toast.message}
      type="success"
      onClose={() => setToast(null)}
    />
  );
}
